import { useLayoutEffect, useRef, useState } from 'react'
import type { CSSProperties } from 'react'
import { scrollIntoFrameThenSettle } from '../../animations/scrollIntoFrame'
import { formatRoomPrice, monzoPayUrl } from '../../rooms'
import type { RoomReservation, RoomType } from '../../types'
import { weddingInfo } from '../../weddingInfo'

interface RoomConfirmationProps {
  reservation: RoomReservation
  /** The invite's code, used as the reference on an online payment. */
  code: string
  /** Used to name the price of the room held; the reservation carries only its name. */
  types: readonly RoomType[]
  /**
   * True when the room was reserved just now, rather than being how the page
   * arrived. Only then is there anything to reveal.
   */
  reveal: boolean
  /** True while any room request is in flight, which disables both buttons. */
  busy: boolean
  /** True only while the room is being given up, for the button's own label. */
  cancelling: boolean
  onChange: () => void
  onRelease: () => void
}

/**
 * How long the card's buttons stay inert after it appears.
 *
 * Long enough to swallow the second half of a double click, or an impatient
 * second press, without being long enough for somebody deliberately reaching for
 * "Change room" to notice. Doubles are conventionally under ~300ms.
 */
const SETTLE_FLOOR_MS = 320

/**
 * What replaces the room list once the invite holds one.
 *
 * The same idea as the food card: four rooms with their descriptions is a lot of
 * page to leave sitting there for somebody who has finished choosing. What they
 * want then is which room is theirs, and a way to change their mind.
 */
export function RoomConfirmation({
  reservation,
  code,
  types,
  reveal,
  busy,
  cancelling,
  onChange,
  onRelease,
}: RoomConfirmationProps) {
  const cardRef = useRef<HTMLDivElement>(null)
  /*
   * True while the card is still being eased into frame.
   *
   * The buttons are inert until it stops, because this card replaces a list some
   * seven hundred pixels tall and then scrolls - so "Change room" arrives under a
   * cursor that was aiming at "Reserve" moments earlier. A second click, or the
   * tail of an impatient double one, would reopen the picker the guest had just
   * closed. Nothing here is worth clicking mid-flight anyway.
   */
  const [settling, setSettling] = useState(false)

  /*
   * Bring the card into frame when it replaces the list.
   *
   * Collapsing four room cards into one takes several hundred pixels out of the
   * page, and the guest's scroll offset stays where it was - leaving them
   * looking below the confirmation they just earned. Same correction as the food
   * summary, and for the same reason.
   *
   * A layout effect so it lands in the same frame as the reflow, not as a
   * visible jump afterwards.
   */
  useLayoutEffect(() => {
    if (!reveal || !cardRef.current) return
    setSettling(true)
    let abandoned = false
    /*
     * The scroll is not enough on its own to time this by.
     *
     * `scrollIntoFrameThenSettle` resolves immediately when the card already
     * happens to be in frame, since there is then nothing to travel - and that is
     * the very case that needs the guard most. No scroll still means the list
     * above collapsed by some five hundred pixels, lifting "Change room" up into
     * where a "Reserve" button was a moment ago, under a cursor that has not
     * moved. So the window has a floor, and the scroll only ever extends it.
     */
    void Promise.all([
      scrollIntoFrameThenSettle(cardRef.current, 'center'),
      new Promise((resolve) => setTimeout(resolve, SETTLE_FLOOR_MS)),
      // Unmounted mid-settle - typically the guest reopening the picker - so
      // there is no longer a card whose buttons need releasing.
    ]).then(() => {
      if (!abandoned) setSettling(false)
    })
    return () => {
      abandoned = true
    }
  }, [reveal])

  const room = types.find((t) => t.id === reservation.room_type_id)
  const price = room?.price_pence ?? 0
  const pay = weddingInfo.rooms.payOptions

  /*
   * Whether there is anywhere to move to.
   *
   * Offering "Change room" when every other kind is taken leads to a list where
   * nothing can be picked - the button promises a choice that does not exist.
   * Their own room does not count: staying put is not a change.
   */
  const canMove = types.some(
    (t) => t.id !== reservation.room_type_id && t.capacity - t.reserved > 0,
  )

  return (
    <div
      ref={cardRef}
      // Matches the food card: no entrance fade when revealing, because the fade
      // and the swap would be competing, and the wrapper's `--reveal-delay` is
      // inherited so it would start from zero anyway.
      className={`border-ink/10 bg-parchment/70 rounded-lg border p-5 shadow-sm sm:p-6 ${
        reveal ? '' : 'animate-reveal'
      }`}
      style={reveal ? undefined : ({ '--reveal-delay': '0ms' } as CSSProperties)}
      role="status"
      aria-live="polite"
    >
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="font-display min-w-0 text-xl">{reservation.room_type_name}</h3>
        <p className="text-sage shrink-0 text-xs tracking-widest uppercase">Reserved</p>
      </div>

      {room && <p className="text-ink-soft mt-2 text-sm">{formatRoomPrice(room.price_pence)}</p>}

      <p className="mt-4 text-sm">
        That is your room for the night. We will pass the name on to the venue.
      </p>

      {/* Settled up, so there is nothing to ask for - just the acknowledgement,
          because a guest who has paid wants to see that we know. */}
      {reservation.paid_at !== null && <p className="text-sage mt-3 text-sm">Paid - thank you.</p>}

      {/* Unpaid, and worth paying for. The free suite is skipped: a £0 payment
          link is an absurdity, and there is nothing to chase. */}
      {reservation.paid_at === null && price > 0 && (
        <div className="border-ink/10 mt-4 border-t pt-4">
          <p className="text-ink-soft text-xs tracking-widest uppercase">
            Paying for it - {pay.intro}
          </p>
          <ul className="text-ink-soft mt-2 space-y-1 text-sm">
            <li>{pay.cash}</li>
            <li>{pay.onLeaving}</li>
          </ul>
          <a
            href={monzoPayUrl(pay.monzoHandle, price, code)}
            target="_blank"
            rel="noreferrer noopener"
            className="border-ink/20 hover:border-claret hover:text-claret mt-3 inline-block rounded-md border px-4 py-2 text-xs tracking-widest uppercase transition-colors"
          >
            {pay.online} - {formatRoomPrice(price).replace(' per night', '')}
          </a>
          {/* Whose account it is, before the button is pressed rather than after.
              A payment link that does not say where the money goes is the kind a
              guest should hesitate over. */}
          <p className="text-ink-soft mt-2 text-xs">{pay.onlineNote}</p>
        </div>
      )}

      <div className="mt-5 flex flex-wrap items-center gap-3">
        {canMove && (
          <button
            type="button"
            onClick={onChange}
            disabled={busy || settling}
            className="border-ink/20 hover:border-claret hover:text-claret rounded-md border px-5 py-2.5 text-sm tracking-widest uppercase transition-colors disabled:opacity-50"
          >
            Change room
          </button>
        )}
        <button
          type="button"
          onClick={onRelease}
          disabled={busy || settling}
          className="text-ink-soft hover:text-claret px-1 text-xs tracking-widest uppercase transition-colors disabled:opacity-50"
        >
          {cancelling ? 'Cancelling…' : 'Cancel the room'}
        </button>
      </div>

      {/* Said plainly, so a missing Change button does not read as a bug. Cancel
          is still there: giving the room up is always allowed, it just cannot be
          swapped for another. */}
      {!canMove && (
        <p className="text-ink-soft mt-3 text-xs">
          This is the last room we have, so there is nothing to move to.
        </p>
      )}

      {/* Directly beneath the two buttons that would strand their money, so it is
          read before either is pressed rather than regretted after. */}
      {reservation.paid_at !== null && (
        <p className="text-claret mt-3 text-xs">{pay.paidWarning}</p>
      )}
    </div>
  )
}
